'use client';

import { useMemo, useState } from 'react';

interface IEnglishIqReorderAnswerProps {
  availableLabel: string;
  disabled: boolean;
  onAnswer: (answer: string) => void;
  selectedLabel: string;
  submitLabel: string;
  tokens: string[];
}

interface ITokenPart {
  id: string;
  index: number;
  token: string;
}

function createTokenParts(tokens: string[]): ITokenPart[] {
  const occurrences = new Map<string, number>();
  return tokens.map((token, index) => {
    const occurrence = occurrences.get(token) ?? 0;
    occurrences.set(token, occurrence + 1);
    return { id: `${token}:${occurrence}`, index, token };
  });
}

function toggleSelectedIndex(selectedIndexes: number[], index: number): number[] {
  return selectedIndexes.includes(index)
    ? selectedIndexes.filter((selectedIndex) => selectedIndex !== index)
    : [...selectedIndexes, index];
}

export function EnglishIqReorderAnswer({
  availableLabel,
  disabled,
  onAnswer,
  selectedLabel,
  submitLabel,
  tokens,
}: IEnglishIqReorderAnswerProps): React.JSX.Element {
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const selectedIndexSet = useMemo(() => new Set(selectedIndexes), [selectedIndexes]);
  const tokenParts = useMemo(() => createTokenParts(tokens), [tokens]);
  const selectedParts = selectedIndexes.flatMap((index) => tokenParts[index] ?? []);
  const answer = selectedParts.map((part) => part.token).join(' ');
  const canSubmit = tokens.length > 0 && selectedIndexes.length === tokens.length;

  return (
    <div className="grid gap-4">
      <div>
        <p className="mb-2 text-sm font-semibold text-muted-foreground">{selectedLabel}</p>
        <div className="flex min-h-12 flex-wrap gap-2" aria-live="polite" dir="ltr">
          {selectedParts.map((part) => (
            <span
              className="rounded-xl border border-violet-500/50 bg-violet-500/10 px-4 py-2 font-medium"
              dir="ltr"
              key={part.id}
            >
              {part.token}
            </span>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-muted-foreground">{availableLabel}</p>
        <div className="flex min-h-12 flex-wrap gap-2" dir="ltr">
          {tokenParts.map((part) => {
            const isSelected = selectedIndexSet.has(part.index);
            return (
              <button
                aria-pressed={isSelected}
                className={`rounded-xl border px-4 py-2 font-medium transition motion-reduce:transition-none disabled:opacity-60 ${isSelected ? 'border-violet-500/50 bg-violet-500/10' : 'border-foreground/15 bg-card hover:border-violet-500/50'}`}
                dir="ltr"
                disabled={disabled}
                key={part.id}
                onClick={() =>
                  setSelectedIndexes((current) => toggleSelectedIndex(current, part.index))
                }
                type="button"
              >
                {part.token}
              </button>
            );
          })}
        </div>
      </div>
      <button
        type="button"
        disabled={disabled || !canSubmit}
        onClick={() => onAnswer(answer)}
        className="min-h-12 rounded-full bg-foreground px-7 py-3 font-semibold text-background transition hover:opacity-85 motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitLabel}
      </button>
    </div>
  );
}
